import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStrings } from '@/i18n/Localization';
import { formatDuration } from '@/media/format';
import { getPreferredRate, playback, setPreferredRate } from '@/media/playbackManager';
import { normalizeWaveform } from '@/media/waveform';
import { PLAYBACK_RATES, WAVEFORM_SAMPLES, type PlaybackRate } from '@/protocol/limits';
import styles from '@/components/media.module.css';

export interface VoicePlayerProps {
  /** Stable identity for the one-active-player rule and auto-advance. */
  playbackKey: string;
  /** Resolves the playable bytes. Called on first play, never on render. */
  resolveSource: () => Promise<string>;
  /** Server waveform when the media is READY; empty for a local recording. */
  waveform: readonly number[];
  durationMs: number | null;
  /** Reports a position for `SetPlaybackPosition`, debounced by the manager. */
  onPosition?: (positionMs: number) => void;
  /** Set by the timeline when auto-advance selects this note. */
  autoPlay?: boolean;
}

/**
 * A voice message player.
 *
 * Section 14 fixes what it must offer: a waveform of 100 normalized samples,
 * the duration, seek, and 1x/1.5x/2x speed — and one active player per account
 * and device, enforced by `media/playbackManager`.
 *
 * The waveform is drawn as an SVG of 100 bars, and the seek control is a real
 * `<input type="range">` layered beside it rather than a click handler on the
 * drawing. A drawing with a pointer handler is invisible to the keyboard, and
 * Section 14 requires keyboard navigation.
 *
 * The audio element is created imperatively rather than rendered, because the
 * source is a short-lived download grant that must not be fetched until the
 * user asks to play: rendering `<audio src>` for every voice note in a
 * conversation would request a grant for each of them on scroll.
 */
export function VoicePlayer(props: VoicePlayerProps) {
  const { t } = useStrings();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sourceRef = useRef<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [durationMs, setDurationMs] = useState(props.durationMs ?? 0);
  const [rate, setRate] = useState<PlaybackRate>(getPreferredRate());
  const [failed, setFailed] = useState(false);

  const bars = useMemo(() => normalizeWaveform(props.waveform), [props.waveform]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setPlaying(false);
  }, []);

  // Registering with the manager is what makes "starting another pauses the
  // previous" work across components that cannot see each other.
  useEffect(() => {
    return () => {
      playback.release(props.playbackKey);
      const element = audioRef.current;
      if (element) {
        element.pause();
        element.src = '';
      }
      if (sourceRef.current !== null) {
        URL.revokeObjectURL(sourceRef.current);
        sourceRef.current = null;
      }
    };
  }, [props.playbackKey]);

  const ensureElement = useCallback(async (): Promise<HTMLAudioElement | null> => {
    if (audioRef.current) {
      return audioRef.current;
    }
    try {
      const source = await props.resolveSource();
      sourceRef.current = source;
      const element = new Audio(source);
      element.preload = 'metadata';
      element.playbackRate = rate;
      element.addEventListener('timeupdate', () => {
        const ms = element.currentTime * 1000;
        setPositionMs(ms);
        playback.recordPosition(props.playbackKey, Math.round(ms));
      });
      element.addEventListener('loadedmetadata', () => {
        if (Number.isFinite(element.duration)) {
          setDurationMs(element.duration * 1000);
        }
      });
      element.addEventListener('ended', () => {
        setPlaying(false);
        setPositionMs(0);
        // Section 14 auto-advances to the next voice note in the open chat
        // while the user keeps playback active. The manager owns the queue.
        playback.finished(props.playbackKey);
      });
      element.addEventListener('error', () => {
        setFailed(true);
        setPlaying(false);
      });
      audioRef.current = element;
      return element;
    } catch {
      setFailed(true);
      return null;
    }
  }, [props, rate]);

  const start = useCallback(async () => {
    playback.claim({ key: props.playbackKey, pause });
    const element = await ensureElement();
    if (!element) {
      return;
    }
    element.playbackRate = rate;
    try {
      await element.play();
      setPlaying(true);
    } catch {
      // Autoplay was refused, or the source is unplayable. Either way the
      // control returns to its stopped state rather than lying about playing.
      setPlaying(false);
    }
  }, [ensureElement, pause, props.playbackKey, rate]);

  // Auto-advance asks this player to start. It is a real user-initiated chain —
  // the user pressed play on an earlier note and has not paused — so the
  // browser's autoplay policy is satisfied by the earlier gesture.
  useEffect(() => {
    if (props.autoPlay) {
      void start();
    }
    // Only the flag drives this; re-running on every `start` identity change
    // would restart playback on an unrelated re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.autoPlay]);

  const toggle = () => {
    if (playing) {
      pause();
      props.onPosition?.(Math.round(positionMs));
      return;
    }
    void start();
  };

  const seek = (value: number) => {
    setPositionMs(value);
    const element = audioRef.current;
    if (element) {
      element.currentTime = value / 1000;
    }
    props.onPosition?.(Math.round(value));
  };

  const cycleRate = () => {
    const index = PLAYBACK_RATES.indexOf(rate);
    const next = PLAYBACK_RATES[(index + 1) % PLAYBACK_RATES.length] ?? 1;
    setRate(next);
    setPreferredRate(next);
    if (audioRef.current) {
      audioRef.current.playbackRate = next;
    }
  };

  const total = durationMs > 0 ? durationMs : (props.durationMs ?? 0);
  const playedFraction = total > 0 ? Math.min(1, positionMs / total) : 0;
  const playedBars = Math.round(playedFraction * WAVEFORM_SAMPLES);

  return (
    <div className={styles.player}>
      <button
        type="button"
        className={styles.playButton}
        onClick={toggle}
        aria-label={playing ? t('player.pause') : t('player.play')}
        disabled={failed}
      >
        <span aria-hidden="true">{playing ? '❚❚' : '▶'}</span>
      </button>

      {/*
        The drawing is decorative: the range input beside it carries the same
        information as a value a screen reader can read and change.
      */}
      <svg
        className={styles.waveform}
        viewBox={`0 0 ${String(WAVEFORM_SAMPLES * 2)} 100`}
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      >
        {bars.map((value, index) => {
          const height = Math.max(4, value);
          return (
            <rect
              key={index}
              className={index < playedBars ? styles.waveBarPlayed : styles.waveBar}
              x={index * 2}
              y={(100 - height) / 2}
              width={1.2}
              height={height}
              rx={0.6}
            />
          );
        })}
      </svg>

      <input
        className={styles.seek}
        type="range"
        min={0}
        max={Math.max(1, Math.round(total))}
        step={100}
        value={Math.round(positionMs)}
        aria-label={t('player.seek')}
        aria-valuetext={t('player.position', {
          position: formatDuration(positionMs),
          duration: formatDuration(total),
        })}
        onChange={(event) => {
          seek(Number(event.target.value));
        }}
      />

      <span className={styles.playerTime}>
        {formatDuration(positionMs)} / {formatDuration(total)}
      </span>

      <button
        type="button"
        className={styles.speed}
        onClick={cycleRate}
        aria-label={t('player.speed')}
      >
        {t('player.speedValue', { rate: String(rate) })}
      </button>

      {failed ? (
        <span className={styles.uploadError} role="status">
          {t('media.downloadFailed')}
        </span>
      ) : null}
    </div>
  );
}
